import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  COST_TYPE,
  type ShippingCostConfig,
  type GatewayCostConfig,
  type FixedCostConfig,
} from '@adtrackify/at-tracking-event-types';

import { isOnboardingEnabledPath } from '@/features/setup/helpers/is-onboarding-enabled-path';
import { useAccountState } from '@/contexts/account/account-context';
import { CostsApiService } from '@/features/costs/api/costs-api-service';
import { useOnboardingFlowStore } from '@/features/setup/stores/use-onboarding-flow-store';
import * as Logger from '@/lib/monitoring/logger';

export function useSyncCostsConfigured(): void {
  const hasCheckedForAccount = useRef<string | null>(null);
  const { pathname } = useLocation();
  const { currentAccount } = useAccountState();
  const pixelId = currentAccount?.pixels?.[0]?.id;
  const costsConfigured = useOnboardingFlowStore((s) => s.costsConfigured);
  const isOnboardingDismissed = useOnboardingFlowStore((s) => s.dismissed);
  const requiredCompletedAt = useOnboardingFlowStore((s) => s.requiredCompletedAt);
  const setCostsConfigured = useOnboardingFlowStore((s) => s.setCostsConfigured);

  const isPastOnboarding = isOnboardingDismissed || requiredCompletedAt !== null;
  const isOnboardingRoute = isOnboardingEnabledPath(pathname);

  useEffect(() => {
    if (!isOnboardingRoute) return;
    if (!pixelId || costsConfigured) return;
    if (isPastOnboarding) return;
    if (hasCheckedForAccount.current === pixelId) return;
    hasCheckedForAccount.current = pixelId;

    async function checkCosts(accountPixelId: string) {
      try {
        const [shipping, gateway, fixed] = await Promise.allSettled([
          CostsApiService.getCostConfig<ShippingCostConfig>(accountPixelId, COST_TYPE.SHIPPING),
          CostsApiService.getCostConfig<GatewayCostConfig>(accountPixelId, COST_TYPE.GATEWAY),
          CostsApiService.getCostConfig<FixedCostConfig>(accountPixelId, COST_TYPE.FIXED),
        ]);

        const hasShipping = shipping.status === 'fulfilled' &&
          ((shipping.value.data as ShippingCostConfig)?.profiles?.length ?? 0) > 0;
        const hasGateway = gateway.status === 'fulfilled' &&
          (gateway.value.data as GatewayCostConfig)?.entries?.length > 0;
        const hasFixed = fixed.status === 'fulfilled' &&
          (fixed.value.data as FixedCostConfig)?.entries?.length > 0;

        if (hasShipping || hasGateway || hasFixed) {
          setCostsConfigured(true);
        }
      } catch (error) {
        Logger.warn('Costs configured detection failed', { error });
      }
    }

    void checkCosts(pixelId);

  }, [isOnboardingRoute, pixelId, costsConfigured, isPastOnboarding, setCostsConfigured]);
}
